import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaMoon, FaSun, FaBell, FaUser, FaBars, FaCar, FaSignOutAlt,
         FaUserPlus, FaCalendarPlus, FaExclamationTriangle, FaMoneyBillWave } from 'react-icons/fa';
import { BiStats, BiCar, BiCalendar, BiUser, BiCog, BiPlus, BiChevronDown } from 'react-icons/bi';
import { useTheme } from '../../context/ThemeContext';
import { useThemeColors } from '../../hooks/useThemeColors';
import { useAuth } from '../../context/AuthContext';
import { Spinner, Badge, Button, Modal } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { isAvailableCar } from '../../utils/statusHelpers';
import './layout.css';

/* ── Data fetching hook (unchanged logic) ─────────────────── */
const useHeaderData = () => {
  const { currentUser } = useAuth();
  const userId = currentUser?.id;
  const [data, setData] = useState({ cars: [], clients: [], reservations: [], maintenanceReminders: [], loading: true, error: null });

  const fetchData = useCallback(async () => {
    if (!userId) { setData(p => ({ ...p, loading: false })); return; }
    try {
      setData(p => ({ ...p, loading: true }));
      const [carsRes, clientsRes, resRes, remRes] = await Promise.all([
        api.get('/cars').catch(() => ({ data: { data: [] } })),
        api.get('/clients').catch(() => ({ data: { data: [] } })),
        api.get('/reservations').catch(() => ({ data: { data: [] } })),
        api.get('/maintenance/maintenance-reminders').catch(() => ({ data: { data: [] } })),
      ]);
      setData({ cars: carsRes.data?.data || [], clients: clientsRes.data?.data || [],
                reservations: resRes.data?.data || [], maintenanceReminders: remRes.data?.data || [],
                loading: false, error: null });
    } catch (e) { setData(p => ({ ...p, loading: false, error: e.message })); }
  }, [userId]);

  useEffect(() => { fetchData(); const t = setInterval(fetchData, 120000); return () => clearInterval(t); }, [fetchData]);
  return { ...data, refetch: fetchData };
};

const calculateStats = (data) => {
  const { cars, clients, reservations, maintenanceReminders } = data;
  const now = new Date();
  const activeRes = reservations.filter(r => {
    if ((r.status || '').toLowerCase() === 'cancelled') return false;
    const start = new Date(r.startDate || r.start_date);
    const end   = new Date(r.endDate   || r.end_date);
    return now >= start && now <= end;
  });
  const availCars = cars.filter(isAvailableCar);
  const sevenDays = new Date(); sevenDays.setDate(sevenDays.getDate() + 7);

  // A reminder's real "done" signal is is_completed (status only ever
  // distinguishes active/canceled in the DB — 'completed' is never a
  // legal value there, so checking for it alone never actually matched).
  const isReminderDone = (rem) => Boolean(rem.is_completed) || rem.status === 'canceled' || rem.status === 'completed';

  // Due: reminders whose trigger date has passed, or a mileage-based
  // reminder whose target mileage has already been reached (not yet
  // completed). Mileage-based reminders have no target_date at all, so
  // without this branch an overdue one produced no signal anywhere once it
  // aged out of the "upcoming" mileage countdown below.
  const dueMaint = maintenanceReminders.filter(rem => {
    if (isReminderDone(rem)) return false;
    if (rem.offset_type === 'date' && rem.date_offset && rem.target_date) {
      const trigger = new Date(rem.target_date);
      trigger.setDate(trigger.getDate() - rem.date_offset);
      return trigger <= now;
    }
    if (rem.target_date) return new Date(rem.target_date) <= now;
    if (rem.reminder_type === 'mileage' && rem.target_mileage) {
      const car = cars.find(c => c.id === rem.car_id);
      const curr = car ? parseFloat(car.currentMileage || car.mileage || 0) : null;
      return curr !== null && curr >= parseFloat(rem.target_mileage);
    }
    return false;
  });

  // Upcoming: reminders due within the next 7 days (not yet triggered, not
  // completed) — honors the same offset-adjusted trigger date as dueMaint,
  // instead of the raw target_date, so a reminder with a large date_offset
  // doesn't skip the "upcoming" warning and jump straight to "due".
  const upcomingMaint = maintenanceReminders.filter(rem => {
    if (isReminderDone(rem)) return false;
    if (!rem.target_date) return false;
    let trigger = new Date(rem.target_date);
    if (rem.offset_type === 'date' && rem.date_offset) {
      trigger = new Date(rem.target_date);
      trigger.setDate(trigger.getDate() - rem.date_offset);
    }
    return trigger > now && trigger <= sevenDays;
  });

  return {
    totalCars: cars.length,
    availableCars: availCars.length,
    totalClients: clients.length,
    activeReservations: activeRes.length,
    totalReservations: reservations.length,
    dueMaintenance: dueMaint.length,
    upcomingMaintenance: upcomingMaint.length,
  };
};

const generateNotifications = (data, stats, mileageAlerts = []) => {
  const t = i18n.t.bind(i18n);
  const notifs = [];

  // 1. Maintenance reminders that are now due — urgent
  if (stats.dueMaintenance > 0) {
    notifs.push({
      id: 'maint-due', type: 'urgent',
      title: t('hdrDueMaintTitle'),
      description: t('hdrDueMaintDesc', { count: stats.dueMaintenance }),
      time: 'hdrNow', icon: '🔧', count: stats.dueMaintenance,
    });
  }

  // 2. Maintenance reminders coming up in the next 7 days — warning
  if (stats.upcomingMaintenance > 0) {
    notifs.push({
      id: 'maint-upcoming', type: 'warning',
      title: t('hdrUpcomingMaintTitle'),
      description: t('hdrUpcomingMaintDesc', { count: stats.upcomingMaintenance }),
      time: 'hdrThisWeek', icon: '📅', count: stats.upcomingMaintenance,
    });
  }

  // 3. Mileage-based maintenance alerts — warning
  mileageAlerts.slice(0, 5).forEach(a => notifs.push({
    id: `km-${a.reminder.id}`, kind: 'mileage', reminderId: a.reminder.id,
    type: 'warning',
    title: t('hdrMileageReminder'),
    description: `${a.carLabel}: ${a.remainingKm.toLocaleString(i18n.language)} km — ${a.serviceLabel}`,
    time: 'hdrNear', icon: '🛢️', count: 1,
  }));

  const prio = { urgent: 0, warning: 1, info: 2, success: 3 };
  return notifs.sort((a, b) => prio[a.type] - prio[b.type]);
};

/* ── Header component ─────────────────────────────────────── */
function Header({ title, onMenuToggle, sidebarOpen }) {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme, responsive } = useTheme();
  const { sidebarColor, sidebarDark } = useThemeColors();
  const { currentUser: authUser, logout: authLogout } = useAuth();

  const { cars, clients, reservations, maintenanceReminders, loading, refetch } = useHeaderData();

  const [userPrefs, setUserPrefs]           = useState({ notificationSound: true });
  const [userNotifs, setUserNotifs]         = useState([]);
  const [userNotifsLoading, setUNL]         = useState(false);
  const [allNotifs, setAllNotifs]           = useState([]);
  const [allNotifsLoading, setANL]          = useState(false);
  const [allNotifsError, setANE]            = useState('');
  const [showNotifsModal, setShowNotifsModal] = useState(false);
  const [acknowledgedKm, setAcknowledgedKm] = useState(new Set());
  const shownKmRef       = useRef(new Set());
  const prevCountRef     = useRef(0);
  const hasLoadedRef     = useRef(false);
  const dismissedSysRef  = useRef(0);
  const KM_KEY        = 'acknowledgedMileageReminders';

  // Dropdown open states
  const [statsOpen,    setStatsOpen]    = useState(false);
  const [notifsOpen,   setNotifsOpen]   = useState(false);
  const [userOpen,     setUserOpen]     = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const closeAll = () => { setStatsOpen(false); setNotifsOpen(false); setUserOpen(false); setQuickAddOpen(false); };

  // Click outside to close
  const statsRef    = useRef(null);
  const notifsRef    = useRef(null);
  const userRef      = useRef(null);
  const quickAddRef  = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (statsRef.current    && !statsRef.current.contains(e.target))    setStatsOpen(false);
      if (notifsRef.current   && !notifsRef.current.contains(e.target))   setNotifsOpen(false);
      if (userRef.current     && !userRef.current.contains(e.target))     setUserOpen(false);
      if (quickAddRef.current && !quickAddRef.current.contains(e.target)) setQuickAddOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const mileageAlerts = useMemo(() => {
    return maintenanceReminders.flatMap(rem => {
      if (!rem?.target_mileage || rem.offset_type !== 'mileage' || !rem.mileage_offset) return [];
      if (acknowledgedKm.has(rem.id)) return [];
      const car = cars.find(c => c.id === rem.car_id);
      if (!car) return [];
      const curr = parseFloat(car.currentMileage || car.mileage || 0);
      const remaining = parseFloat(rem.target_mileage) - curr;
      if (remaining <= 0 || remaining > parseFloat(rem.mileage_offset)) return [];
      return [{ reminder: rem, remainingKm: remaining,
                carLabel: `${car.brand} ${car.model} (${car.licensePlate || car.plate || 'N/A'})`,
                serviceLabel: rem.service_type || "l'entretien" }];
    }).sort((a, b) => a.remainingKm - b.remainingKm);
  }, [maintenanceReminders, cars, acknowledgedKm]);

  const stats = calculateStats({ cars, clients, reservations, maintenanceReminders });

  const sysNotifs = useMemo(() =>
    generateNotifications({ reservations, maintenanceReminders, cars }, stats, mileageAlerts)
      .map(n => ({ ...n, source: 'system', suppressCountBadge: true })),
    [reservations, maintenanceReminders, cars, stats, mileageAlerts, i18n.language]
  );

  const feedNotifs = useMemo(() =>
    userNotifs.map(n => ({ id: `fb-${n.id}`, type: n.type || 'info', title: n.title || 'Notification',
      description: n.message, time: n.createdAt, icon: '💬', count: 1, isRead: n.isRead, source: 'feedback' })),
    [userNotifs]
  );

  const allDropdownNotifs = useMemo(() => {
    const map = new Map();
    [...feedNotifs.slice(0, 5), ...sysNotifs].forEach(n => { if (!map.has(n.id)) map.set(n.id, n); });
    return Array.from(map.values());
  }, [feedNotifs, sysNotifs]);

  const unreadCount = userNotifs.filter(n => !n.isRead).length;
  const sysAlertCount = sysNotifs.filter(n => n.type === 'urgent' || n.type === 'warning').length;
  const unseenSysCount = Math.max(0, sysAlertCount - dismissedSysRef.current);
  const totalBadgeCount = unreadCount + unseenSysCount;

  const notifBadgeColor = () => {
    if (allDropdownNotifs.some(n => n.type === 'urgent')) return '#ef4444';
    if (allDropdownNotifs.some(n => n.type === 'warning')) return '#f59e0b';
    return '#3b82f6';
  };

  const fetchUserNotifs = useCallback(async () => {
    if (!authUser?.id) return;
    try {
      setUNL(true);
      const res = await api.get('/notifications');
      if (res.data?.success) setUserNotifs((res.data.data || []).map(i => ({ ...i, isRead: Boolean(i.is_read ?? i.isRead) })));
    } catch {}
    finally { setUNL(false); }
  }, [authUser?.id]);

  const markAllRead = useCallback(async () => {
    if (!authUser?.id || !userNotifs.length) return;
    try { await api.post('/notifications/mark-all-read'); setUserNotifs(p => p.map(i => ({ ...i, isRead: true }))); } catch {}
  }, [authUser?.id, userNotifs.length]);

  const fetchAllNotifs = useCallback(async () => {
    if (!authUser?.id) return;
    try {
      setANL(true); setANE('');
      const res = await api.get('/notifications/all');
      if (res.data?.success) setAllNotifs((res.data.data || []).map(i => ({ ...i, isRead: Boolean(i.is_read ?? i.isRead) })));
      else setANE(res.data?.error || i18n.t('errMsgLoadData'));
    } catch { setANE(i18n.t('errMsgLoadData')); }
    finally { setANL(false); }
  }, [authUser?.id]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setAllNotifs(p => p.map(i => i.id === id ? { ...i, isRead: true } : i));
      setUserNotifs(p => p.map(i => i.id === id ? { ...i, isRead: true } : i));
    } catch {}
  };

  const ackKm = useCallback((id) => {
    setAcknowledgedKm(prev => {
      const next = new Set(prev); next.add(id);
      try { localStorage.setItem(KM_KEY, JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);

  const loadPrefs = () => {
    try {
      const s = localStorage.getItem('userSettings');
      if (s) { const p = JSON.parse(s); setUserPrefs(prev => ({ ...prev, notificationSound: p.notificationSound ?? prev.notificationSound })); }
    } catch {}
  };

  const playSound = () => {
    if (!userPrefs.notificationSound) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC(); const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.type = 'sine'; osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.3); osc.onended = () => ctx.close();
    } catch {}
  };

  useEffect(() => { fetchUserNotifs(); const t = setInterval(fetchUserNotifs, 120000); return () => clearInterval(t); }, [fetchUserNotifs]);
  useEffect(() => { const h = () => fetchUserNotifs(); window.addEventListener('user-notifications-updated', h); return () => window.removeEventListener('user-notifications-updated', h); }, [fetchUserNotifs]);
  useEffect(() => { if (!authUser?.id) { setUserNotifs([]); setAllNotifs([]); } }, [authUser?.id]);
  useEffect(() => { loadPrefs(); const h = () => loadPrefs(); window.addEventListener('userSettingsUpdated', h); window.addEventListener('storage', h); return () => { window.removeEventListener('userSettingsUpdated', h); window.removeEventListener('storage', h); }; }, []);
  useEffect(() => { try { const s = localStorage.getItem(KM_KEY); if (s) { const p = JSON.parse(s); if (Array.isArray(p)) setAcknowledgedKm(new Set(p)); } } catch {} }, []);
  useEffect(() => { if (!hasLoadedRef.current) { prevCountRef.current = totalBadgeCount; hasLoadedRef.current = true; return; } if (totalBadgeCount > prevCountRef.current) playSound(); prevCountRef.current = totalBadgeCount; }, [totalBadgeCount]);
  useEffect(() => {
    if (loading) return;
    mileageAlerts.forEach(a => {
      const key = a.reminder.id;
      if (shownKmRef.current.has(key)) return;
      shownKmRef.current.add(key);
      toast.info(
        <div className="d-flex flex-column gap-2">
          <div>🔧 {a.carLabel}: {a.remainingKm.toLocaleString('fr-FR')} km avant {a.serviceLabel}</div>
          <Button size="sm" variant="outline-secondary" onClick={() => { ackKm(a.reminder.id); toast.dismiss(`km-${a.reminder.id}`); }}>{i18n.t('hdrMarkSeen')}</Button>
        </div>,
        { containerId: 'hdr-reminders', autoClose: 7000, toastId: `km-${a.reminder.id}` }
      );
    });
  }, [mileageAlerts, loading, ackKm]);
  useEffect(() => { const h = () => refetch(); window.addEventListener('carMileageUpdated', h); return () => window.removeEventListener('carMileageUpdated', h); }, [refetch]);
  useEffect(() => {
    if (!authUser?.isMasterAdmin) return;
    const h = (e) => {
      const d = e.detail; if (!d?.id) return;
      setUserNotifs(p => p.some(i => i.id === d.id) ? p : [{ ...d, isRead: false }, ...p]);
    };
    window.addEventListener('supportNotification', h); return () => window.removeEventListener('supportNotification', h);
  }, [authUser?.isMasterAdmin]);

  const displayName = authUser?.firstName && authUser?.lastName
    ? `${authUser.firstName} ${authUser.lastName}` : authUser?.username || t('user');
  const displayEmail = authUser?.email || '';
  const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const formatTime = (timeStr) => {
    const timeKeys = ['hdrNow','hdrToProcess','hdrWaiting','hdrThisWeek','hdrToday','hdrAttention','hdrNear'];
    if (!timeStr || timeKeys.includes(timeStr)) return timeStr ? t(timeStr) : '';
    const d = new Date(timeStr); return isNaN(d) ? timeStr : d.toLocaleString(i18n.language);
  };

  const handleNotifsToggle = () => {
    if (!notifsOpen) {
      markAllRead();
      dismissedSysRef.current = sysAlertCount;
    }
    setNotifsOpen(o => !o);
    setStatsOpen(false); setUserOpen(false); setQuickAddOpen(false);
  };

  return (
    <>
      <ToastContainer containerId="hdr-reminders" position="bottom-right" autoClose={7000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss pauseOnHover theme={theme === 'dark' ? 'dark' : 'light'} style={{ bottom: 20, right: 20, zIndex: 99999 }} />

      <header
        className="hdr"
        dir="ltr"
        style={{ background: 'var(--color-surface, #fff)', borderBottom: '1px solid var(--color-border, #e5e7eb)', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}
      >
        {/* Left */}
        <div className="hdr__left">
          {/* Hamburger (mobile) */}
          <button className="hdr__hamburger d-lg-none" onClick={() => onMenuToggle && onMenuToggle(!sidebarOpen)} aria-label="Menu">
            <FaBars />
          </button>

          {/* Brand */}
          <div className="hdr__brand">
            <div className="hdr__brand-icon"><FaCar /></div>
            <span className="hdr__brand-text">{responsive?.isMobile ? '' : (title ? t(title) : 'Rentora')}</span>
          </div>

          {/* Quick add (desktop) — one entry point instead of 4 separate
              pill buttons competing with the brand for space. */}
          <div className="hdr__quick-actions d-none d-lg-flex" ref={quickAddRef} style={{ position: 'relative' }}>
            <button
              className="hdr__quickadd-btn"
              onClick={() => { setQuickAddOpen(o => !o); setStatsOpen(false); setNotifsOpen(false); setUserOpen(false); }}
            >
              <BiPlus size={16} />
              {t('hdrQuickAdd', 'Quick add')}
              <BiChevronDown size={14} />
            </button>
            {quickAddOpen && (
              <div className="hdr__dropdown-panel hdr__dropdown-panel--left hdr__quickadd-panel">
                <Link to="/ajouter-client" className="hdr__user-menu-item" onClick={() => setQuickAddOpen(false)}>
                  <span className="hdr__user-menu-icon"><FaUserPlus /></span> {t('hdrAddClient')}
                </Link>
                <Link to="/formulaire-voiture" className="hdr__user-menu-item" onClick={() => setQuickAddOpen(false)}>
                  <span className="hdr__user-menu-icon"><FaCar /></span> {t('hdrAddCar')}
                </Link>
                <Link to="/reservations" className="hdr__user-menu-item" onClick={() => setQuickAddOpen(false)}>
                  <span className="hdr__user-menu-icon"><FaCalendarPlus /></span> {t('hdrAddReservation')}
                </Link>
                <Link to="/entretiens?action=addExpense" className="hdr__user-menu-item" onClick={() => setQuickAddOpen(false)}>
                  <span className="hdr__user-menu-icon"><FaMoneyBillWave /></span> {t('hdrAddExpense')}
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Center — desktop stats */}
        <div className="hdr__center d-none d-lg-flex">
          <div className="hdr__stats">
            <div className="hdr__stat">
              <span className="hdr__stat-value">{loading ? '—' : stats.totalCars}</span>
              <span className="hdr__stat-label">{t('hdrCars')}</span>
            </div>
            <div className="hdr__stat-divider" />
            <div className="hdr__stat">
              <span className="hdr__stat-value">{loading ? '—' : stats.activeReservations}</span>
              <span className="hdr__stat-label">{t('hdrActiveReservations')}</span>
            </div>
            <div className="hdr__stat-divider" />
            <div className="hdr__stat">
              <span className="hdr__stat-value">{loading ? '—' : stats.totalClients}</span>
              <span className="hdr__stat-label">{t('hdrClients')}</span>
            </div>
          </div>
        </div>

        {/* Right */}
        <div className="hdr__right">
          {/* Mobile stats toggle */}
          <div className="hdr__dropdown-wrapper d-lg-none" ref={statsRef}>
            <button className="hdr__stats-toggle" onClick={() => { setStatsOpen(o => !o); setNotifsOpen(false); setUserOpen(false); setQuickAddOpen(false); }}>
              <BiStats /> <span>{stats.totalCars}</span>
            </button>
            {statsOpen && (
              <div className="hdr__dropdown-panel hdr__dropdown-panel--left hdr__stats-panel">
                <div className="hdr__dp-header">
                  <h6>📊 {t('hdrStats')}</h6>
                </div>
                <div className="hdr__stats-grid">
                  <div className="hdr__stats-card">
                    <div className="hdr__stats-card-icon" style={{ background: '#3b82f6' }}><BiCar /></div>
                    <div><div className="hdr__stats-card-value">{loading ? '—' : stats.totalCars}</div><div className="hdr__stats-card-label">{t('hdrCars')}<br/><small style={{ color: '#22c55e' }}>{stats.availableCars} {t('hdrStatsDispo')}</small></div></div>
                  </div>
                  <div className="hdr__stats-card">
                    <div className="hdr__stats-card-icon" style={{ background: '#f59e0b' }}><BiCalendar /></div>
                    <div><div className="hdr__stats-card-value">{loading ? '—' : stats.activeReservations}</div><div className="hdr__stats-card-label">{t('reservations')}</div></div>
                  </div>
                  <div className="hdr__stats-card">
                    <div className="hdr__stats-card-icon" style={{ background: '#06b6d4' }}><BiUser /></div>
                    <div><div className="hdr__stats-card-value">{loading ? '—' : stats.totalClients}</div><div className="hdr__stats-card-label">{t('hdrClients')}</div></div>
                  </div>
                  {stats.dueMaintenance > 0 && (
                    <div className="hdr__stats-card">
                      <div className="hdr__stats-card-icon" style={{ background: '#ef4444' }}><FaExclamationTriangle /></div>
                      <div><div className="hdr__stats-card-value">{stats.dueMaintenance}</div><div className="hdr__stats-card-label">{t('hdrDueMaint')}</div></div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Theme toggle */}
          <button className="hdr__icon-btn" onClick={toggleTheme} title={theme === 'light' ? t('hdrDarkMode') : t('hdrLightMode')}>
            {theme === 'light' ? <FaMoon /> : <FaSun />}
          </button>

          {/* Notifications */}
          <div className="hdr__dropdown-wrapper" ref={notifsRef}>
            <button className="hdr__icon-btn" onClick={handleNotifsToggle} aria-label="Notifications">
              <FaBell />
              {!userNotifsLoading && totalBadgeCount > 0 && (
                <span className="hdr__notif-badge" style={{ background: notifBadgeColor(), borderColor: sidebarColor }}>
                  {totalBadgeCount > 9 ? '9+' : totalBadgeCount}
                </span>
              )}
            </button>
            {notifsOpen && (
              <div className="hdr__dropdown-panel">
                <div className="hdr__dp-header d-flex justify-content-between align-items-center">
                  <h6>🔔 {t('hdrNotifications')}</h6>
                  <small>{totalBadgeCount} {totalBadgeCount !== 1 ? t('hdrUnreadPlural') : t('hdrUnread')}</small>
                </div>
                <div className="hdr__dp-body">
                  {(loading || userNotifsLoading) ? (
                    <div className="hdr__notif-empty"><Spinner animation="border" size="sm" variant="primary" /><span>{t('hdrLoading')}</span></div>
                  ) : allDropdownNotifs.length > 0 ? allDropdownNotifs.map(n => (
                    <div key={n.id} className="hdr__notif-item">
                      <div className={`hdr__notif-icon ${n.type === 'urgent' ? 'bg-danger' : n.type === 'warning' ? 'bg-warning' : n.type === 'success' ? 'bg-success' : 'bg-info'}`}>
                        <span>{n.icon}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="hdr__notif-title">{n.title}</p>
                        <p className="hdr__notif-desc">{n.description}</p>
                        <span className="hdr__notif-time">{formatTime(n.time)}</span>
                        {n.kind === 'mileage' && (
                          <div className="mt-1">
                            <Button size="sm" variant="outline-secondary" onClick={() => ackKm(n.reminderId)}>{t('hdrMarkSeen')}</Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )) : (
                    <div className="hdr__notif-empty">
                      <FaBell style={{ fontSize: 28, opacity: 0.35 }} />
                      <span>{t('hdrAllUpToDate')}</span>
                    </div>
                  )}
                </div>
                <div className="hdr__dp-footer">
                  <button className="hdr__dp-footer-link" onClick={() => { setNotifsOpen(false); setShowNotifsModal(true); fetchAllNotifs(); }}>
                    <FaBell style={{ marginRight: 6 }} /> {t('hdrViewAll')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="hdr__dropdown-wrapper" ref={userRef}>
            <button className="hdr__user-pill" onClick={() => { setUserOpen(o => !o); setNotifsOpen(false); setStatsOpen(false); setQuickAddOpen(false); }}>
              <div className="hdr__user-avatar-sm">{initials || <FaUser />}</div>
              <span className="hdr__user-pill-name d-none d-sm-block">
                {displayName.length > 14 ? displayName.slice(0, 14) + '…' : displayName}
              </span>
            </button>
            {userOpen && (
              <div className="hdr__dropdown-panel hdr__user-panel">
                <div className="hdr__user-panel-header">
                  <div className="hdr__user-panel-avatar">{initials || <FaUser />}</div>
                  <div className="hdr__user-panel-name">{displayName}</div>
                  {displayEmail && <div className="hdr__user-panel-email">{displayEmail}</div>}
                </div>
                <div className="hdr__divider" />
                <Link to="/profile" className="hdr__user-menu-item" onClick={() => setUserOpen(false)}>
                  <span className="hdr__user-menu-icon"><FaUser /></span> {t('hdrProfile')}
                </Link>
                <Link to="/settings" className="hdr__user-menu-item" onClick={() => setUserOpen(false)}>
                  <span className="hdr__user-menu-icon"><BiCog /></span> {t('hdrSettings')}
                </Link>
                <div className="hdr__divider" />
                <button className="hdr__user-menu-item hdr__user-menu-item--danger" onClick={() => { setUserOpen(false); authLogout(); }}>
                  <span className="hdr__user-menu-icon hdr__user-menu-icon--danger"><FaSignOutAlt /></span> {t('hdrLogout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* All notifications modal */}
      <Modal show={showNotifsModal} onHide={() => setShowNotifsModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>{t('hdrAllNotifs')}</Modal.Title></Modal.Header>
        <Modal.Body>
          {allNotifsLoading && <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>}
          {!allNotifsLoading && allNotifsError && <div className="alert alert-danger mb-0">{allNotifsError}</div>}
          {!allNotifsLoading && !allNotifsError && allNotifs.length === 0 && <div className="text-center text-muted py-4">{t('hdrNoNotifs')}</div>}
          {!allNotifsLoading && !allNotifsError && allNotifs.length > 0 && (
            <div className="d-flex flex-column gap-3">
              {allNotifs.map(n => (
                <div key={n.id} className={`p-3 rounded border ${n.isRead ? '' : 'border-primary'}`} style={{ background: 'var(--color-surface)' }}>
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div className="fw-semibold" style={{ fontSize: 13 }}>{n.title || 'Notification'}</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>{n.message}</div>
                      {n.createdAt && <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>{new Date(n.createdAt).toLocaleString('fr-FR')}</div>}
                    </div>
                    {!n.isRead && <Button size="sm" variant="outline-primary" onClick={() => markRead(n.id)}>{t('hdrMarkRead')}</Button>}
                  </div>
                </div>
              ))}
              {sysNotifs.length > 0 && (
                <div className="mt-2">
                  <div className="text-muted mb-2" style={{ fontSize: 12 }}>{t('hdrSystemNotifs')}</div>
                  {sysNotifs.map(n => (
                    <div key={n.id} className="p-3 rounded border mb-2" style={{ background: 'var(--color-bg)' }}>
                      <div className="fw-semibold" style={{ fontSize: 13 }}>{n.title}</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>{n.description}</div>
                      <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>{formatTime(n.time)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNotifsModal(false)}>{t('hdrClose')}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Header;
